/**
 * React App entry point, handling mobile views and desktop views.
 */

import * as React from 'react';
import './App.css';

import * as MediaQuery from 'react-responsive';
import * as Icon from 'react-fontawesome';

import SideBar from 'react-sidebar';

import { PostStore } from '../stores/PostStore';
import { observer, inject } from 'mobx-react';
import { SearchBox } from './Inputs/SearchBox';
import { Blacklist } from './Inputs/Blacklist/Blacklist';
import { observable } from 'mobx';
import '../themes/Dark.css';
import { Stream } from './Stream/Stream';
import { Scroller, CustomScrollBars } from './Stream/ScrollBar';
// import { first, chunk, flattenDeep } from 'lodash';
import { Section } from './Inputs/Section/Section';
import { TagList } from './Info/TagList';
import { StreamStore } from '../stores/StreamStore';

const Spinner = require('react-spinkit');



const Loader = () => (
    <div className="Stream-loader">
        <Spinner name="ball-pulse-sync" className="Theme-background" />
    </div>
);
export enum Themes {
    DARK,
    LIGHT,
}

interface SectionStates {
    blacklist: boolean;
    tags: boolean;
}

const SECTION_OPEN_STATE = 'section_open_state';

const openSectionsMaybe = localStorage.getItem(SECTION_OPEN_STATE);
const openSections: SectionStates = openSectionsMaybe
    ? JSON.parse(openSectionsMaybe)
    : {
          blacklist: false,
          tags: false,
      };

@inject(allStores => ({
    postStore: allStores.postStore as PostStore,
    streamStore: allStores.streamStore as StreamStore,
}))
@observer
class App extends React.Component<{ postStore?: PostStore; streamStore?: StreamStore }> {
    @observable hasMore: boolean = false;
    @observable sidebar = false;
    @observable tagbar = false;
    @observable currentPost = 0;
    @observable sections: SectionStates = openSections;
    scroll = 0;

    /**
     * Toggles a section and saves the open state into localstorage
     * 
     * @memberof App
     */
    setSection = (section: string, state: boolean) => {
        this.sections[section] = state;
        localStorage.setItem(SECTION_OPEN_STATE, JSON.stringify(this.sections));
    }

    /**
     * Current post in view.
     * 
     * @memberof App
     */
    postChange = (i: number) => {
        this.currentPost = i;
    }

    /**
     * Last scroll position.
     * Used for keeping the scroll position the same on a view re-render (desktop size to mobile size)
     * @memberof App
     */
    scrollChange = (pos: number) => {
        this.scroll = pos;
    }

    /**
     * Mobile branch of the app, simplified version.
     * 
     * @memberof App
     */
    mobileView = (sfwButton: JSX.Element, posts: JSX.Element, blacklist: JSX.Element) => (
        <SideBar
            sidebar={
                <div className="App-mobile-sidebar">
                    <div className="App-mobile-header">
                        <span className="App-mobile-header-text">
                            Settings
                        </span>
                        <span className="App-mobile-sidebar-close" onClick={() => (this.sidebar = false)}>
                            <Icon name="times" /> 
                        </span>
                    </div>


                    {sfwButton}

                    {blacklist}
                </div>
            }
            open={this.sidebar}
            onSetOpen={() => (this.sidebar = !this.sidebar)}
            styles={{
                sidebar: {
                    backgroundColor: '#1c1c1c', // todo themes
                },
            }}
        >
            <div className="App-mobile">
                <div className="App-mobile-inputs Theme-background">
                    <div className="App-mobile-inputs-inner"> 
                    <div className="App-mobile-inputs-options App-mobile-input-left">
                        <div className="App-mobile-big-buttons">
                            <span
                                className="App-mobile-input Theme-background"
                                onClick={() => (this.sidebar = !this.sidebar)}
                            >
                                <Icon name="bars" />
                            </span>
                        </div>
                    </div>
                    <div className="App-mobile-search">
                        <SearchBox
                            tags={this.props.postStore!.flatTags}
                            chippers={this.props.postStore!.searchQuery.chips}
                        />
                    </div>

                    <span
                        onClick={() => this.props.postStore!.initiateSearch(0, true)}
                    >
                    <div className="App-mobile-inputs-options App-mobile-input-right">
                        <div className="App-mobile-big-buttons">
                            <span
                                className="App-mobile-input Theme-background"
                            >
                                <Icon name="search" />
                            </span>
                        </div>
                    </div>
                    </span>
                    </div>
                </div>
            <div className="App-mobile-feed">{posts}</div>
            <div className="App-mobile-bottom">
                    {this.props.postStore!.searching && <Loader />}
                    {this.props.postStore!.lastSearchEmpty && (
                        <div style={{ textAlign: 'center' }}> no search results found :( </div>
                    )}

            </div>

            </div>

        </SideBar>
    )

    /**
     * Desktop view of the app, full information in view all the time.
     * 
     * @memberof App
     */
    desktopView = (sfwButton: JSX.Element, posts: JSX.Element, blacklist: JSX.Element) => (
        <div>
            <div className="fl App-desktop">
                <div className="App-sidebar">
                    <div className="App-desktop-search">
                        <SearchBox
                            tags={this.props.postStore!.flatTags}
                            chippers={this.props.postStore!.searchQuery.chips}
                        />
                    </div>

                    <div className="App-desktop-options App-desktop-">
                        <button
                            className="App-search-button App-desktop-search-button Theme-background"
                            onClick={() => this.props.postStore!.initiateSearch(0, true)}
                        >
                            Search
                        </button>
                        {sfwButton}
                        <div className="App-desktop-sections">
                            <CustomScrollBars>
                                {blacklist}
                                <Section
                                    name={
                                        <span className="App-header">
                                            <Icon name="tags" /> Tags
                                        </span>
                                    }
                                    style={{
                                        borderBottom: '1px solid white',
                                    }}
                                    opened={this.sections.tags}
                                    onClick={() => this.setSection('tags', !this.sections.tags)}
                                >
                                    <div style={{ height: '30vh' }}>
                                        <TagList active={this.props.streamStore!.activePost} />
                                    </div>
                                </Section>
                            </CustomScrollBars>
                        </div>
                    </div>
                </div>
                <div className="App-desktop-stream-area"> 
                    <div
                        style={{
                            height: '95vh',
                            marginLeft: '35px',
                        }}
                        className="App-desktop-stream"
                    >
                        {posts}
                    </div>
                    <div className="App-desktop-stream-bottom">
                        {this.props.postStore!.searching && <Loader />}

                    </div>


                </div>
            </div>
        </div>
    )

    /**
     * The fun render method! 
     * 
     * @returns 
     * @memberof App
     */
    render() {
        const blacklist = (height: string) => (
            <Section
                name={
                    <span className="App-header">
                        <Icon name="ban" /> Blacklist{' '}
                    </span>
                }
                style={{
                    borderBottom: '1px solid #550000',
                    marginBottom: 10,
                }}
                borderColour="#550000"
                opened={this.sections.blacklist}
                onClick={() => this.setSection('blacklist', !this.sections.blacklist)}
            >
                <div style={{ height }}>
                    <CustomScrollBars>
                        <Blacklist />
                    </CustomScrollBars>
                </div>
            </Section>
        );


        const posts = (
                <Scroller
                    current={0}
                    onNewPost={this.postChange}
                    scroll={this.scroll}
                    onScroll={this.scrollChange}
                >
                    <Stream
                        active={this.currentPost}
                        setActive={this.postChange}
                        /* posts={this.currentDogs}
                        getPosts={this.getMoreDogs}
                        hasMore={this.hasMore}
                        active={this.currentPost} */
                    />
                </Scroller>

        );

        const sfwButton = (
            <div className="App-inputs-checkbox">
                <input
                    name="sfw"
                    type="checkbox"
                    className="Theme-background"
                    checked={this.props.postStore!.searchQuery.sfw}
                    onClick={() => {
                        const { searchQuery } = this.props.postStore!;
                        searchQuery.sfw = !searchQuery.sfw;
                    }}
                />
                <label htmlFor="sfw"> SFW Images </label>
            </div>
        );

        return (
            <div
                className="Theme-background App-fullsize"
                style={{
                    display: 'flex',
                    paddingTop: '15px',
                    overflow: 'hidden',
                }}
            >
                <MediaQuery minWidth={1080}>
                    {this.desktopView(sfwButton, posts, blacklist('30vh'))}
                </MediaQuery>

                <MediaQuery maxWidth={1079}>
                    {this.mobileView(sfwButton, posts, blacklist('50vh'))}
                </MediaQuery>
            </div>
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/App.tsx